import {
	AdminSearchContainer,
	AdminSettingLandingPageHeader
} from "pages/Preferences/AdminSettings/AdminSettingLandingPage.styles.ts";
import SuggestedSearch from "components/Common/SuggestedSearch/SuggestedSearch.tsx";
import { Fragment, SyntheticEvent, useContext, useEffect, useMemo, useState } from "react";
import { TableBody } from "@mui/material";
import SortableTableHead, { HeadCell } from "components/Common/Table/SortableTableHead.tsx";
import { useHandleTableSort } from "hooks/useHandleTableSort.ts";
import { models } from "types/api/viewModels.ts";
import {
	AccountNameText,
	AccountNumberText,
	AckEmailsQuantityText,
	AdminSearchAccountBullet,
	AdminSearchNoResultsFoundHeader,
	AdminSearchNoResultsFoundSubheader,
	AllAccountsText,
	BillToQuantityText,
	SearchAccountsText,
	UnavailableDataPlaceholderText
} from "constants/text.ts";
import { StyledTableCell, StyledTableWrapper } from "components/Common/Table/StyledTable.styles.ts";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { useGetAccountsQuery } from "features/api/accountApi.ts";
import { FilteredRequestsTableWrapper } from "components/CustomQuote/CustomQuoteRequestTable.styles.ts";
import { ClickableTableRow } from "components/AdminSettings/AdminSearchTable.styles.ts";
import {
	AccountSettingsPageContent,
	AccountSettingsSearchWrapper
} from "pages/Preferences/Accounts/styles/AllAccountSettingsPage.styles.ts";
import { NoResultsBox } from "pages/Preferences/NoResultsBox.tsx";
import TableSkeleton from "components/Common/Table/TableSkeleton/TableSkeleton.tsx";
import { useNavigate } from "react-router";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import APIErrorCustomBanner from "components/Common/APIErrorCustomBanner/APIErrorCustomBanner.tsx";
import { UserPermission } from "types/declarations/enums.ts";

const AllAccountSettingsPage = () => {
	const { data: accounts, isLoading } = useGetAccountsQuery(null);
	const { data: userDetails } = useGetUserDetailsQuery();
	const isAdmin = userDetails?.permissions?.find((x) => x === UserPermission.ADMIN) ?? false;
	const navigate = useNavigate();
	const accountsTableHeader = useMemo(
		() =>
			[
				{ id: "accountName", label: AccountNameText, sortable: true },
				{ id: "accountNumber", label: AccountNumberText },
				{ id: "billTos", label: BillToQuantityText },
				{ id: "email", label: AckEmailsQuantityText },
				{ id: "open", label: "" }
			].map((cell) => ({
				...cell,
				disablePadding: false,
				numeric: false,
				sortable: cell.sortable ?? false
			})) satisfies HeadCell[],
		[]
	);
	const { configurationsData } = useContext(ConfigurationsContext);
	const isUserPreferenceEnabled = configurationsData?.featureFlags?.userPreferenceEnabled ?? false;

	const { getComparator, handleRequestSort, order, orderBy } = useHandleTableSort("accountName", "asc");
	const [searchedAccounts, setSearchedAccounts] = useState<models["CustomerAccountViewModel"][]>(accounts ?? []);
	useEffect(() => {
		setSearchedAccounts(accounts ?? []);
	}, [accounts]);
	const filteredAccounts = useMemo(() => {
		return searchedAccounts ? searchedAccounts.slice().sort(getComparator(order, orderBy)) : [];
	}, [searchedAccounts, getComparator, order, orderBy]);
	const filteredHeadCells = useMemo(() => accountsTableHeader, [accountsTableHeader]);
	const handleSearch = (_: SyntheticEvent, v: string | null) => {
		const searchedAccounts =
			accounts?.filter((account) =>
				[account.accountNumber, account.accountName]
					.filter(Boolean)
					.join(" - ")
					.toLowerCase()
					.includes(v?.toLowerCase() ?? "")
			) ?? [];
		setSearchedAccounts(searchedAccounts.length > 0 ? searchedAccounts : []);
	};

	const getAckRecipients = (account: models["CustomerAccountViewModel"]) => {
		return account.billTos?.flatMap((billTo) => billTo.acknowledgements?.recipients ?? []) ?? [];
	};

	const GetNewRequestTableRows = (account: models["CustomerAccountViewModel"]) => {
		const accountTableCells = [
			{
				testId: "admin-search-table-name",
				props: { maxWidth: "240px" },
				content: account?.accountName ?? UnavailableDataPlaceholderText
			},
			{
				testId: "admin-search-table-number",
				props: { minWidth: "120px" },
				content: account?.accountNumber ?? UnavailableDataPlaceholderText
			},
			{
				testId: "admin-search-table-type",
				props: { maxWidth: "140px" },
				content: account?.billTos?.length ?? UnavailableDataPlaceholderText
			},
			{
				testId: "admin-search-table-email",
				props: { maxWidth: "140px" },
				content: getAckRecipients(account).length ?? UnavailableDataPlaceholderText
			}
		];

		return (
			<Fragment>
				{accountTableCells.map(({ testId, props, content }) => (
					<StyledTableCell
						key={testId}
						data-testid={testId}
						{...props}
					>
						{content}
					</StyledTableCell>
				))}

				<StyledTableCell
					fixedWidth="40px"
					isLastIndex
				>
					<ChevronRight />
				</StyledTableCell>
			</Fragment>
		);
	};

	const renderContent = () => {
		if (isLoading) {
			return (
				<AdminSearchContainer>
					<TableSkeleton />
				</AdminSearchContainer>
			);
		}

		if (filteredAccounts && filteredAccounts.length > 0) {
			return (
				<FilteredRequestsTableWrapper>
					<StyledTableWrapper
						data-testid="new-requests-table"
						aria-label="New Requests Table"
					>
						<SortableTableHead
							orderBy={orderBy}
							order={order}
							onRequestSort={handleRequestSort}
							headCells={filteredHeadCells}
						/>
						<TableBody>
							{filteredAccounts.map((account: models["CustomerAccountViewModel"]) => (
								<ClickableTableRow
									key={`${account.accountNumber} - ${account.accountName}`}
									data-testid={`account-${account?.accountNumber}`}
									onClick={() => {
										navigate("/admin-settings/accounts/" + account.accountId + "/billtos");
									}}
								>
									{GetNewRequestTableRows(account)}
								</ClickableTableRow>
							))}
						</TableBody>
					</StyledTableWrapper>
				</FilteredRequestsTableWrapper>
			);
		}

		return (
			<NoResultsBox
				headerText={AdminSearchNoResultsFoundHeader}
				subheaderText={AdminSearchNoResultsFoundSubheader}
				bulletList={[AdminSearchAccountBullet]}
			/>
		);
	};

	if (!isAdmin || !isUserPreferenceEnabled) {
		return (
			<div>
				<APIErrorCustomBanner statusCode={403} />
			</div>
		);
	}

	return (
		<div>
			<AdminSettingLandingPageHeader>
				<h1>{AllAccountsText}</h1>
			</AdminSettingLandingPageHeader>
			<AccountSettingsPageContent>
				<AccountSettingsSearchWrapper>
					<SuggestedSearch
						ariaLabel="Admin Account Search"
						dataTestId="admin-accounts-search-input"
						dropDownOpenOnChar={0}
						placeholder={SearchAccountsText}
						id="admin-accounts-search-input"
						searchOptions={
							accounts?.map((account) =>
								[account.accountNumber, account.accountName].filter(Boolean).join(" - ")
							) ?? []
						}
						searchValue=""
						handleSearch={handleSearch}
						size="small"
						hasButton
					/>
				</AccountSettingsSearchWrapper>
				{renderContent()}
			</AccountSettingsPageContent>
		</div>
	);
};

export default AllAccountSettingsPage;
